// AuthContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { encryptPassword } from "../utils/CryptHelper";

const AuthContext = createContext(null);
const TOKEN_COOKIE = "authToken";

/* ---------- helpers pentru cookies ---------- */

function setCookie(name, value, days) {
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie =
    name + "=" + encodeURIComponent(value || "") + expires + "; path=/";
}

function getCookie(name) {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let c of ca) {
    let cookie = c;
    while (cookie.charAt(0) === " ") cookie = cookie.substring(1, cookie.length);
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
    }
  }
  return null;
}

function eraseCookie(name) {
  document.cookie =
    name + "=; Max-Age=-99999999; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;";
}

/* ------------------------------------------------ */

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState(null);

  /* 🔁 Hidratare la mount: încercăm să citim token-ul din cookie
     și să luăm profilul din /security/profile */
  useEffect(() => {
    const existingToken = getCookie(TOKEN_COOKIE);
    if (!existingToken) return;

    const fetchProfile = async () => {
      setLoading(true);
      try {
        const profileReq = await fetch(`api/security/profile`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: existingToken,
          },
        });

        if (!profileReq.ok) {
          throw new Error("Eroare la obținerea profilului");
        }

        const profile = await profileReq.json().catch(() => null);
        const content = profile?.Content || {};

        setUser({
          email: content.Email || content.email || "",
          company: content.Company || content.Denumire || "",
          codFiscal:
            content.Cod_fiscal || content.CodFiscal || content.codFiscal || "",
        });
        setToken(existingToken);
      } catch (err) {
        console.error("Hydration profile error:", err);
        eraseCookie(TOKEN_COOKIE); // token invalid → îl ștergem
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const login = useCallback(async ({ email, password, codFiscal }) => {
    setLoading(true);
    setAuthError(null);
    try {
      const encryptedPassword = await encryptPassword(password);

      const res = await fetch(`api/security/auth`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Email: email,
          Password: encryptedPassword,
          CodVAT: codFiscal,
        },
      });

      if (!res.ok) {
        throw new Error(`Login failed (${res.status})`);
      }

      const data = await res.json().catch(() => null);
      console.log("Login response data:", data);

      if (!data || data.isError) {
        throw new Error(data?.Message || "Eroare de autentificare");
      }

      const tokenFromApi = data.Content?.token;
      if (!tokenFromApi) {
        throw new Error("Token lipsă în răspunsul de login");
      }

      // ✅ salvăm token în cookie + state
      setCookie(TOKEN_COOKIE, tokenFromApi, 7); // 7 zile, ajustează cum vrei
      setToken(tokenFromApi);

      // Luăm profilul
      const profileReq = await fetch(`api/security/profile`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: tokenFromApi,
        },
      });

      if (!profileReq.ok) {
        throw new Error("Eroare la obținerea profilului");
      }

      const profile = await profileReq.json().catch(() => null);
      console.log("Profile response data:", profile);

      const content = profile?.Content || {};

      setUser({
        email: content.Email || email,
        company: content.Company || content.Denumire || "",
        codFiscal:
          content.Cod_fiscal || content.CodFiscal || codFiscal || "",
      });

      return { success: true, data: data || null };
    } catch (err) {
      console.error(err);
      setAuthError(err.message || "Eroare de autentificare");
      return { success: false, error: err };
    } finally {
      setLoading(false);
    }
  }, []);

  const signup = useCallback(
    async ({
      email,
      password,
      codFiscal,
      denumire = "",
      adresa = "",
      judet = "",
      localitate = "",
      nrLicenses = 1,
      database = "Test",
    }) => {
      setLoading(true);
      setAuthError(null);
      try {
        const encryptedPassword = await encryptPassword(password);

        const body = {
          Cod_fiscal: codFiscal,
          Denumire: denumire,
          Adresa: adresa,
          Judet: judet,
          Localitate: localitate,
          Nr_Licenses: nrLicenses,
          Email: email,
          Password: encryptedPassword,
          Database: database,
        };

        const res = await fetch(`api/companies/new`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          throw new Error(`Signup failed (${res.status})`);
        }

        const data = await res.json().catch(() => null);

        // aici poți decide dacă vrei să faci auto-login sau doar să setezi user minim
        // setUser({
        //   email,
        //   company: denumire,
        //   codFiscal,
        // });

        return { success: true, data: data || null };
      } catch (err) {
        console.error(err);
        setAuthError(err.message || "Eroare la înregistrare");
        return { success: false, error: err };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const logout = () => {
    setUser(null);
    setToken(null);
    eraseCookie(TOKEN_COOKIE);
  };

  const value = {
    user,
    token,
    loading,
    authError,
    login,
    signup,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
